import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { authService } from '../../services/authService';
import { User, LoginCredentials, RegisterData, LoginResponse } from '../../types/auth';

// Import token refresh utilities
let tokenRefreshUtils: {
  scheduleTokenRefresh?: (token: string) => void;
  clearTokenRefreshTimer?: () => void;
} = {};

// Lazy load to avoid circular dependency
const getTokenRefreshUtils = () => {
  if (!tokenRefreshUtils.scheduleTokenRefresh) {
    const api = require('../../services/api');
    tokenRefreshUtils = {
      scheduleTokenRefresh: api.scheduleTokenRefresh,
      clearTokenRefreshTimer: api.clearTokenRefreshTimer,
    };
  }
  return tokenRefreshUtils;
};

const clearPersistedAuthState = () => {
  localStorage.removeItem('accessToken');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('user');

  try {
    const utils = getTokenRefreshUtils();
    utils.clearTokenRefreshTimer?.();
  } catch (e) {
    console.error('Failed to clear token refresh timer:', e);
  }
};

const shouldClearAuthOnRefreshFailure = (reason: unknown): boolean => {
  const message = String(reason || '').toLowerCase();
  return ['revoked', 'expired', 'invalid', 'unauthorized', 'login again', 'no refresh token'].some((fragment) =>
    message.includes(fragment)
  );
};

const extractErrorMessage = (error: unknown, fallback: string) => {
  if (!error) {
    return fallback;
  }

  if (typeof error === 'string') {
    return error;
  }

  if (error instanceof Error && error.message) {
    return error.message;
  }

  // Handle axios error response with nested error object
  const maybeAxios = error as { 
    response?: { 
      data?: { 
        message?: string; 
        detail?: string | { message?: string };
        error?: { message?: string; code?: string };
      } 
    } 
  };
  
  const data = maybeAxios.response?.data;
  if (data) {
    // Check for error.message (backend format)
    if (data.error?.message) {
      return data.error.message;
    }
    // Check for direct message
    if (data.message) {
      return data.message;
    }
    // Check for detail (can be string or object)
    if (typeof data.detail === 'string') {
      return data.detail;
    }
    if (typeof data.detail === 'object' && data.detail?.message) {
      return data.detail.message;
    }
  }
  
  return fallback;
};

interface AuthState {
  user: User | null;
  accessToken: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
}

// Load user from localStorage
const loadUserFromStorage = (): User | null => {
  try {
    const userStr = localStorage.getItem('user');
    return userStr ? JSON.parse(userStr) : null;
  } catch {
    return null;
  }
};

const initialState: AuthState = {
  user: loadUserFromStorage(),
  accessToken: localStorage.getItem('accessToken'),
  refreshToken: localStorage.getItem('refreshToken'),
  isAuthenticated: !!localStorage.getItem('accessToken'),
  isLoading: false,
  error: null,
};

export const login = createAsyncThunk(
  'auth/login',
  async (credentials: LoginCredentials, { rejectWithValue }) => {
    try {
      const response = await authService.login(credentials);
      return response;
    } catch (error: any) {
      return rejectWithValue(extractErrorMessage(error, 'Login failed'));
    }
  }
);

export const register = createAsyncThunk(
  'auth/register',
  async (data: RegisterData, { rejectWithValue }) => {
    try {
      const response = await authService.register(data);
      return response;
    } catch (error: any) {
      return rejectWithValue(extractErrorMessage(error, 'Registration failed'));
    }
  }
);

export const logout = createAsyncThunk('auth/logout', async () => {
  await authService.logout();
});

export const refreshAccessToken = createAsyncThunk(
  'auth/refresh',
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState() as { auth: AuthState };
      if (!state.auth.refreshToken) {
        throw new Error('No refresh token');
      }
      const response = await authService.refreshToken(state.auth.refreshToken);
      return response;
    } catch (error: any) {
      return rejectWithValue(extractErrorMessage(error, 'Token refresh failed'));
    }
  }
);

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setCredentials: (state, action: PayloadAction<LoginResponse>) => {
      if (action.payload) {
        state.user = action.payload.user;
        state.accessToken = action.payload.access_token;
        state.refreshToken = action.payload.refresh_token;
        state.isAuthenticated = true;
        localStorage.setItem('accessToken', action.payload.access_token);
        localStorage.setItem('refreshToken', action.payload.refresh_token);
        localStorage.setItem('user', JSON.stringify(action.payload.user));
        // Save user's timezone preference for timezone utility
        if (action.payload.user?.timezone) {
          localStorage.setItem('user_timezone', action.payload.user.timezone);
        }
        // Schedule proactive token refresh
        try {
          const utils = getTokenRefreshUtils();
          if (utils.scheduleTokenRefresh) {
            utils.scheduleTokenRefresh(action.payload.access_token);
          }
        } catch (e) {
          console.error('Failed to schedule token refresh:', e);
        }
      }
    },
    setUser: (state, action: PayloadAction<User>) => {
      state.user = action.payload;
      localStorage.setItem('user', JSON.stringify(action.payload));
      // Save user's timezone preference for timezone utility
      if (action.payload?.timezone) {
        localStorage.setItem('user_timezone', action.payload.timezone);
      }
    },
    clearCredentials: (state) => {
      state.user = null;
      state.accessToken = null;
      state.refreshToken = null;
      state.isAuthenticated = false;
      state.error = null;
      clearPersistedAuthState();
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Login
      .addCase(login.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.isLoading = false;
        // If 2FA is required, do NOT commit tokens — user must verify TOTP first
        if (action.payload && !action.payload.two_fa_required) {
          state.user = action.payload.user;
          state.accessToken = action.payload.access_token;
          state.refreshToken = action.payload.refresh_token;
          state.isAuthenticated = true;
          localStorage.setItem('accessToken', action.payload.access_token);
          localStorage.setItem('refreshToken', action.payload.refresh_token);
          localStorage.setItem('user', JSON.stringify(action.payload.user));
          // Schedule proactive token refresh
          try {
            const utils = getTokenRefreshUtils();
            if (utils.scheduleTokenRefresh) {
              utils.scheduleTokenRefresh(action.payload.access_token);
            }
          } catch (e) {
            console.error('Failed to schedule token refresh:', e);
          }
        }
      })
      .addCase(login.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Register — no tokens returned; user must verify email then log in
      .addCase(register.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(register.fulfilled, (state) => {
        state.isLoading = false;
        // Registration no longer auto-logs in — email verification required
      })
      .addCase(register.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Logout
      .addCase(logout.fulfilled, (state) => {
        state.user = null;
        state.accessToken = null;
        state.refreshToken = null;
        state.isAuthenticated = false;
        clearPersistedAuthState();
      })
      .addCase(logout.rejected, (state) => {
        state.user = null;
        state.accessToken = null;
        state.refreshToken = null;
        state.isAuthenticated = false;
        clearPersistedAuthState();
      })
      // Refresh Token
      .addCase(refreshAccessToken.fulfilled, (state, action) => {
        if (action.payload) {
          state.accessToken = action.payload.access_token;
          // Also update refresh token if provided (token rotation)
          if (action.payload.refresh_token) {
            state.refreshToken = action.payload.refresh_token;
            localStorage.setItem('refreshToken', action.payload.refresh_token);
          }
          localStorage.setItem('accessToken', action.payload.access_token);
          // Schedule next proactive token refresh
          try {
            const utils = getTokenRefreshUtils();
            if (utils.scheduleTokenRefresh) {
              utils.scheduleTokenRefresh(action.payload.access_token);
            }
          } catch (e) {
            console.error('Failed to schedule token refresh:', e);
          }
        }
      })
      .addCase(refreshAccessToken.rejected, (state, action) => {
        const reason = action.payload || action.error.message || 'Token refresh failed';
        state.error = String(reason);

        if (shouldClearAuthOnRefreshFailure(reason)) {
          state.user = null;
          state.accessToken = null;
          state.refreshToken = null;
          state.isAuthenticated = false;
          clearPersistedAuthState();
        }
      });
  },
});

export const { setCredentials, setUser, clearCredentials, clearError } = authSlice.actions;
export default authSlice.reducer;
